import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * Analytics Module Navigation Items
 */
export const AnalyticsSidebar = ({ isCollapsed, getNavItemStyle, userRole }) => {
    const navItems = [
        { to: '/analytics/intelligence', label: 'Intelligence', icon: 'dashboard' },
        { to: '/analytics/forecasting', label: 'Forecasting', icon: 'trending_up' },
        { to: '/analytics/calls', label: 'Calls', icon: 'call' },
        { to: '/analytics/reports', label: 'Reports', icon: 'assessment' },
        { to: '/analytics/meetings', label: 'Meetings', icon: 'groups' },
        { to: '/analytics/leads', label: 'Leads', icon: 'track_changes' },
        { to: '/analytics/deals', label: 'Deals', icon: 'handshake' },
        { to: '/analytics/quality', label: 'Quality', icon: 'verified_user' },
        { to: '/analytics/documents', label: 'Documents', icon: 'menu_book' },
        ...(userRole === 'superadmin' ? [{ to: '/analytics/inventory', label: 'Inventory', icon: 'inventory_2' }] : [])
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%', marginBottom: '8px' }}>
            {navItems.map((item) => (
                <NavLink
                    key={item.to}
                    to={item.to}
                    style={({ isActive }) => ({
                        ...getNavItemStyle(isActive),
                        background: isActive ? 'rgba(108, 99, 255, 0.12)' : 'transparent',
                        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                        borderRadius: '8px',
                        fontFamily: "'Lexend Deca', sans-serif"
                    })}
                >
                    {({ isActive }) => (
                        <>
                            <ObliqueIcon
                                name={item.icon}
                                size={22}
                                isActive={isActive}
                                color={isActive ? '#6C63FF' : 'currentColor'}
                                strokeScale={1.1}
                                style={{ flexShrink: 0, transition: 'all 0.15s ease' }}
                            />
                            {!isCollapsed && (
                                <span
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: isActive ? 600 : 400,
                                        letterSpacing: '-0.01em',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: isActive ? '#6C63FF' : 'inherit'
                                    }}
                                >
                                    {item.label}
                                </span>
                            )}
                        </>
                    )}
                </NavLink>
            ))}
        </div>
    );
};

/**
 * LMS Module Navigation Items — Notion Academic System Design
 * Supports dual perspective: Trainee (Learner) vs Trainer (Manager)
 */
export const LMSSidebar = ({ isCollapsed, getNavItemStyle, ThemeCalendarIcon, lmsRole = 'learner' }) => {
    const location = useLocation();

    const traineeItems = [
        { section: 'Learning' },
        { to: '/lms', label: 'Courses', icon: 'menu_book' },
        { to: '/lms/playbook', label: 'Sales Playbook', icon: 'auto_stories' },
        { section: 'Assessments' },
        { to: '/lms?tab=assessments', label: 'Assessment', icon: 'quiz' },
        { to: '/lms?tab=assessments&view=scores', label: 'My Scores', icon: 'workspace_premium' },
        { to: '/lms/planner', label: 'Training Calendar', icon: 'calendar_today' }
    ];

    const trainerItems = [
        { section: 'Cohort Oversight' },
        { to: '/lms?tab=tracks', label: 'Curriculum Overview', icon: 'dashboard' },
        { to: '/lms?tab=trainees', label: 'Trainee Roster & Seats', icon: 'group' },
        { to: '/lms?tab=trainees&action=assign', label: 'Assign Modules', icon: 'assignment_ind' },
        { section: 'Grading & Analytics' },
        { to: '/lms?tab=assessments', label: 'Review Queue & Tests', icon: 'fact_check' },
        { to: '/lms?tab=assessments&view=all', label: 'All Trainee Scorecards', icon: 'bar_chart' },
        { to: '/lms/courses', label: 'Course Catalog Editor', icon: 'edit_document' }
    ];

    const currentItems = lmsRole === 'trainer' ? trainerItems : traineeItems;

    return (
        <div className="flex flex-col gap-1 w-full pb-4">
            {/* Nav Items (Role switch is consolidated exclusively at sidebar bottom) */}
            {currentItems.map((item, idx) => {
                if (item.section) {
                    if (isCollapsed) return null;
                    return (
                        <div key={idx} className="px-2 pt-3 pb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                                {item.section}
                            </span>
                        </div>
                    );
                }

                const isCourses = item.to === '/lms' && location.pathname === '/lms' && (!location.search || location.search.includes('tab=tracks') || location.search.includes('tab=courses'));
                const isActive = isCourses ||
                    (item.to !== '/lms' && location.pathname + location.search === item.to) ||
                    (item.to.includes('tab=') && location.search.includes(item.to.split('?')[1]?.split('&')[0])) ||
                    (location.pathname === item.to);

                return (
                    <NavLink
                        key={item.to + idx}
                        to={item.to}
                        title={isCollapsed ? item.label : undefined}
                        className={`
                            flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-all no-underline
                            ${isCollapsed ? 'justify-center px-0' : 'justify-start'}
                            ${isActive
                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold border-l-2 border-[var(--accent)] pl-2'
                                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] font-medium'
                            }
                        `}
                    >
                        <div className="w-[18px] h-[18px] shrink-0 flex items-center justify-center text-[var(--text-secondary)]">
                            <ObliqueIcon name={item.icon} size={16} isActive={isActive} />
                        </div>
                        {!isCollapsed && (
                            <span className="truncate select-none leading-none">
                                {item.label}
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </div>
    );
};

/**
/**
 * Manager/Settings Module Navigation Items
 */
export const ManagerSidebar = ({ isCollapsed, getNavItemStyle, userRole, location }) => {
    const sections = [
        {
            title: 'Personal Settings',
            items: [
                { to: '/settings/general', label: 'General', icon: 'settings' },
                { to: '/settings/notifications', label: 'Notifications', icon: 'notifications' },
                { to: '/settings/user-preferences', label: 'User Preferences', icon: 'person' },
                { to: '/settings/data-transfers', label: 'Data Transfers', icon: 'assignment_turned_in' },
            ]
        },
        {
            title: 'Workspace Administration',
            items: [
                { to: '/settings/admin/company', label: 'Workspace & Branding', icon: 'domain' },
                { to: '/settings/admin/users', label: 'Users & Permissions', icon: 'group' },
                { to: '/settings/admin/roles', label: 'Roles & Access', icon: 'admin_panel_settings' },
            ]
        },
        {
            title: 'Quality Management',
            items: [
                { to: '/settings/qms/rubrics', label: 'Rubrics & Scorecards', icon: 'list_alt' },
                { to: '/settings/qms/audit', label: 'Audit Settings', icon: 'assignment_turned_in' },
                { to: '/settings/qms/calibration', label: 'Calibration Rules', icon: 'balance' },
                { to: '/settings/qms/compliance', label: 'Compliance Rules', icon: 'gavel' },
                { to: '/settings/qms/rca-templates', label: 'RCA Templates', icon: 'hub' },
                { to: '/settings/qms/coaching-templates', label: 'Coaching Templates', icon: 'psychology' },
            ]
        },
        {
            title: 'Automation & Orbit',
            items: [
                { to: '/settings/automation/workflows', label: 'Workflow Builder', icon: 'schema' },
                { to: '/settings/automation/triggers', label: 'Triggers', icon: 'bolt' },
                { to: '/settings/automation/notifications', label: 'Alerts', icon: 'campaign' },
            ]
        }
    ];

    return (
        <div className="flex flex-col gap-1 w-full pb-4">
            {sections.map((sec, sIdx) => (
                <div key={sIdx} className="flex flex-col gap-0.5">
                    {!isCollapsed && sec.title && (
                        <div className="px-2 pt-3 pb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                                {sec.title}
                            </span>
                        </div>
                    )}
                    {sec.items.map((item) => {
                        const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                title={isCollapsed ? item.label : undefined}
                                className={`
                                    flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-all no-underline
                                    ${isCollapsed ? 'justify-center px-0' : 'justify-start'}
                                    ${isActive
                                        ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold border-l-2 border-[var(--accent)] pl-2'
                                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] font-medium'
                                    }
                                `}
                            >
                                <div className="w-[18px] h-[18px] shrink-0 flex items-center justify-center text-[var(--text-secondary)]">
                                    <ObliqueIcon name={item.icon} size={16} isActive={isActive} />
                                </div>
                                {!isCollapsed && (
                                    <span className="truncate select-none leading-none">
                                        {item.label}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

/**
 * HR / People Operations Navigation Items
 */
export const HRSidebar = ({ isCollapsed, location }) => {
    const items = [
        { to: '/hr', label: 'Employee Directory', icon: 'groups' },
        { to: '/hr?tab=onboarding', label: 'Onboarding Pipeline', icon: 'person_add' },
        { to: '/hr?tab=leaves', label: 'Leaves & Attendance', icon: 'event_available' },
        { to: '/hr?tab=reviews', label: 'Performance Reviews', icon: 'star' }
    ];

    return (
        <div className="flex flex-col gap-1 w-full pb-4">
            {!isCollapsed && (
                <div className="px-2 pt-2 pb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                        People Operations
                    </span>
                </div>
            )}
            {items.map((item) => {
                const isActive = location?.pathname === item.to || 
                    (location?.pathname === '/hr' && item.to.includes('tab=') && location?.search?.includes(item.to.split('?')[1])) ||
                    (location?.pathname === '/hr' && !item.to.includes('tab=') && (!location?.search || location?.search === ''));
                return (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        title={isCollapsed ? item.label : undefined}
                        className={`
                            flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-all no-underline
                            ${isCollapsed ? 'justify-center px-0' : 'justify-start'}
                            ${isActive
                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold border-l-2 border-[var(--accent)] pl-2'
                                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] font-medium'
                            }
                        `}
                    >
                        <div className="w-[18px] h-[18px] shrink-0 flex items-center justify-center text-[var(--text-secondary)]">
                            <ObliqueIcon name={item.icon} size={16} isActive={isActive} />
                        </div>
                        {!isCollapsed && (
                            <span className="truncate select-none leading-none">
                                {item.label}
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </div>
    );
};

/**
 * Finance Navigation Items
 */
export const FinanceSidebar = ({ isCollapsed, location }) => {
    const items = [
        { to: '/finance', label: 'Financial Cockpit', icon: 'payments' },
        { to: '/finance?tab=invoices', label: 'Invoices & Billing', icon: 'receipt_long' },
        { to: '/finance?tab=subscriptions', label: 'Subscription Tiers', icon: 'card_membership' },
        { to: '/finance?tab=expenses', label: 'Operating Expenses', icon: 'trending_down' }
    ];

    return (
        <div className="flex flex-col gap-1 w-full pb-4">
            {!isCollapsed && (
                <div className="px-2 pt-2 pb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                        Finance & Capital
                    </span>
                </div>
            )}
            {items.map((item) => {
                const isActive = location?.pathname === item.to || 
                    (location?.pathname === '/finance' && item.to.includes('tab=') && location?.search?.includes(item.to.split('?')[1])) ||
                    (location?.pathname === '/finance' && !item.to.includes('tab=') && (!location?.search || location?.search === ''));
                return (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        title={isCollapsed ? item.label : undefined}
                        className={`
                            flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-all no-underline
                            ${isCollapsed ? 'justify-center px-0' : 'justify-start'}
                            ${isActive
                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold border-l-2 border-[var(--accent)] pl-2'
                                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] font-medium'
                            }
                        `}
                    >
                        <div className="w-[18px] h-[18px] shrink-0 flex items-center justify-center text-[var(--text-secondary)]">
                            <ObliqueIcon name={item.icon} size={16} isActive={isActive} />
                        </div>
                        {!isCollapsed && (
                            <span className="truncate select-none leading-none">
                                {item.label}
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </div>
    );
};

/**
 * Developer Navigation Items
 */
export const DeveloperSidebar = ({ isCollapsed, location }) => {
    const items = [
        { to: '/developer', label: 'System Uptime & Ops', icon: 'dns' },
        { to: '/developer?tab=keys', label: 'API Keys & Secrets', icon: 'key' },
        { to: '/developer?tab=webhooks', label: 'Webhooks & Events', icon: 'webhook' },
        { to: '/developer?tab=logs', label: 'System Logs', icon: 'terminal' }
    ];

    return (
        <div className="flex flex-col gap-1 w-full pb-4">
            {!isCollapsed && (
                <div className="px-2 pt-2 pb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                        Developer Core
                    </span>
                </div>
            )}
            {items.map((item) => {
                const isActive = location?.pathname === item.to || 
                    (location?.pathname === '/developer' && item.to.includes('tab=') && location?.search?.includes(item.to.split('?')[1])) ||
                    (location?.pathname === '/developer' && !item.to.includes('tab=') && (!location?.search || location?.search === ''));
                return (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        title={isCollapsed ? item.label : undefined}
                        className={`
                            flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-all no-underline
                            ${isCollapsed ? 'justify-center px-0' : 'justify-start'}
                            ${isActive
                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold border-l-2 border-[var(--accent)] pl-2'
                                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] font-medium'
                            }
                        `}
                    >
                        <div className="w-[18px] h-[18px] shrink-0 flex items-center justify-center text-[var(--text-secondary)]">
                            <ObliqueIcon name={item.icon} size={16} isActive={isActive} />
                        </div>
                        {!isCollapsed && (
                            <span className="truncate select-none leading-none">
                                {item.label}
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </div>
    );
};

